import NewReleasesIcon from "@mui/icons-material/NewReleases";
import { Box, Grid } from "@mui/material";
import { useCallback } from "react";
import {
  AutocompleteArrayInput,
  BulkDeleteButton,
  Create,
  Datagrid,
  DateTimeInput,
  DeleteButton,
  Edit,
  EditButton,
  FunctionField,
  Labeled,
  Link,
  List,
  NumberInput,
  Pagination,
  ReferenceArrayInput,
  required,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  ShowButton,
  SimpleForm,
  TextField,
  TextInput,
  Toolbar,
  useCreate,
  useNotify,
  useRecordContext,
  useRedirect,
  useUpdate,
} from "react-admin";
import Currency from "../components/currency";
import LocalisedDateField from "../components/localisedDateField";
import LocalisedDateInputPreview from "../components/localisedDateInputPreview";
import ProductSelectionArrayInput from "../components/ProductSelectionArrayInput";
import StartDateEndDateInput from "../components/startDateEndDateInput";
import TextWithCopy from "../components/TextWithCopy";

function sort_by_id() {
  return function (elem1: any, elem2: any) {
    if (elem1.id < elem2.id) {
      return -1;
    } else if (elem1.id > elem2.id) {
      return 1;
    } else {
      return 0;
    }
  };
}

const CouponCodeCategrories = [
  { id: "COUPON", name: "COUPON" },
  { id: "PRAXISDIENSTE", name: "PRAXISDIENSTE" },
  { id: "DENTIST", name: "DENTIST" },
  { id: "NEWSLETTER", name: "NEWSLETTER" },
  { id: "INFLUENCER", name: "INFLUENCER" },
  { id: "ORGANIC", name: "ORGANIC" },
  { id: "META", name: "META" },
  { id: "SEARCH", name: "SEARCH" },
  { id: "DISPLAY", name: "DISPLAY" },
  { id: "REFERRAL", name: "REFERRAL" },
  { id: "PROFESSIONAL", name: "PROFESSIONAL" },
  { id: "GOOGLE", name: "GOOGLE" },
  { id: "SOCIAL_MEDIA", name: "SOCIAL_MEDIA" },
  { id: "AFFILIATE", name: "AFFILIATE" },
  { id: "TEST", name: "TEST" },
  { id: "SUPPORT", name: "SUPPORT" },
  { id: "EVENTS", name: "EVENTS" },
  { id: "PR", name: "PR" },
  { id: "COOPERATIONS", name: "COOPERATIONS" },
].sort(sort_by_id());

const CountriesSupported = [
  { id: "DE", name: "DE" },
  { id: "AT", name: "AT" },
  { id: "GB", name: "GB" },
  { id: "FR", name: "FR" },
  { id: "IT", name: "IT" },
].sort(sort_by_id());

const PromotionCodeFilters = [
  //   <TextInput label="ID" source="pk" alwaysOn />,
  <TextInput label="Coupon code" source="couponCode__icontains" alwaysOn />,
  <TextInput label="Description" source="description__icontains" alwaysOn />,
  <SelectInput source="category" choices={CouponCodeCategrories} alwaysOn />,
  <NumberInput label="Precent" source="percentage" alwaysOn />,
  <NumberInput label="Value" source="value" alwaysOn />,
  <StartDateEndDateInput source="startDate" />,
  <StartDateEndDateInput source="endDate" />,
  <SelectInput
    label="Currency"
    source="currency__icontains"
    choices={[
      { id: "eur", name: "EUR" },
      { id: "gbp", name: "GBP" },
      { id: "usd", name: "USD" },
    ]}
    alwaysOn
  />,
  <ReferenceArrayInput label="Countries" source="country__icontains" reference="locales" perPage={100}>
    <AutocompleteArrayInput
      validate={required()}
      label="Countries"
      optionText={(record) => `${record.name} - ${record.countryEmoji}`}
      optionValue="country"
      filterToQuery={(query) => {
        return { name__icontains: query };
      }}
    />
  </ReferenceArrayInput>,
  <ReferenceArrayInput label="Products" source="products__in" reference="v2Products" perPage={100}>
    <AutocompleteArrayInput
      validate={required()}
      label="Products"
      optionText={(record) => `${record.name} - ${record.productId}`}
      optionValue="productId"
      filterToQuery={(query) => {
        return { name__icontains: query };
      }}
    />
  </ReferenceArrayInput>,
  // <TextInput label="Email" source="email__icontains" defaultValue="" />,
  // <DateInput label="Date from" source="created_at__gte" alwaysOn defaultValue="" />,
  // <DateInput label="Date to" source="created_at__lte" alwaysOn defaultValue="" />,
];

const PromotionCodeBulkActionButtons = () => (
  <>
    <BulkDeleteButton label="Archive & Deactivate" />
  </>
);

const MyPagination = () => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} />;

const MyToolbar = (props: any) => {
  const redirect = useRedirect();
  const notify = useNotify();
  const record = useRecordContext();
  return record.active ? (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      <SaveButton label="Update" />
      <span>
        Created <LocalisedDateField record={record} field="createdAt" showTime={true} />
      </span>
      <DeleteButton label="Archive & Deactivate" sx={{ textAlign: "right" }} />
    </Toolbar>
  ) : (
    <Toolbar sx={{ justifyContent: "space-between" }}>
      <span>
        Created <LocalisedDateField record={record} field="createdAt" showTime={true} />
      </span>
      <span>
        Deactivated <LocalisedDateField record={record} field="modifiedAt" showTime={true} />
      </span>
    </Toolbar>
  );
};

// active?: Maybe<Scalars["Boolean"]>;
// category?: Maybe<Scalars["String"]>;
// couponCode?: Maybe<Scalars["String"]>;
// createdAt?: Maybe<Scalars["DateTime"]>;
// currency?: Maybe<Scalars["String"]>;
// description?: Maybe<Scalars["String"]>;
// id: Scalars["ID"];
// modifiedAt?: Maybe<Scalars["DateTime"]>;
// percentage?: Maybe<Scalars["Float"]>;
// stripeCouponCodeId?: Maybe<Scalars["String"]>;
// stripePromotionCodeId?: Maybe<Scalars["String"]>;
// value?: Maybe<Scalars["Int"]>;

export const PromotionCodeList = () => (
  <List filters={PromotionCodeFilters} pagination={<MyPagination />} sort={{ field: "created_at", order: "DESC" }}>
    <Datagrid bulkActionButtons={<PromotionCodeBulkActionButtons />}>
      {/* rowClick="edit" */}
      <TextField source="category" />
      <FunctionField
        label="Coupon Code"
        render={(record: { couponCode: string; endDate?: string; active?: boolean }) => {
          const isDeactivated = record.active === false;
          const isExpired = isDeactivated || Boolean(record.endDate && new Date(record.endDate) < new Date());
          const iconTitle = isDeactivated ? "Deactivated" : "Expired";
          return (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <TextWithCopy
                source="couponCode"
                displayText="Coupon code"
                containerStyle={{
                  padding: "2px 8px",
                  border: "1px dashed #d0d5dd",
                  borderRadius: 6,
                  backgroundColor: "#fafafa",
                }}
                textStyle={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                }}
              />
              {isExpired ? (
                <span title={iconTitle}>
                  <NewReleasesIcon sx={{ color: "red", fontSize: 18, ml: 0.5, verticalAlign: "middle" }} />
                </span>
              ) : null}
            </Box>
          );
        }}
      />
      <TextField source="description" />
      <TextField source="country" />
      <FunctionField
        textAlign="right"
        label="Discount"
        render={(record: { percentage?: number; value?: number; currency?: string }) => {
          if (typeof record.percentage === "number" && record.percentage > 0) {
            return (
              <Box
                component="span"
                sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
              >
                {record.percentage}%
              </Box>
            );
          }
          if (typeof record.value === "number" && record.value > 0 && record.currency) {
            return (
              <Box
                component="span"
                sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" }}
              >
                <Currency price={record.value} currency={record.currency} />
              </Box>
            );
          }
          return "-";
        }}
      />
      <LocalisedDateField field="startDate" label="Start Date" compact />
      <LocalisedDateField field="endDate" label="End Date" compact />
      <FunctionField
        textAlign="right"
        label="Order Count"
        render={(record: { orderCount: number; couponCode: string }) => {
          return (
            <Link
              to={`/orders?filter=${JSON.stringify({ couponcode__icontains: encodeURIComponent(record.couponCode) })}`}
            >
              {record.orderCount}
            </Link>
          );
        }}
      />
      <FunctionField label="Actions" render={(record: any) => (record.active ? <EditButton /> : <ShowButton />)} />
    </Datagrid>
  </List>
);

const PromotionCodeCreateEditForm = ({ defaultStartDate = false }: { defaultStartDate?: boolean }) => {
  return (
    <Grid container columnSpacing={2}>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <SelectInput source="category" choices={CouponCodeCategrories} fullWidth={true} defaultValue={"COUPON"} />
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <Labeled label="Code" fullWidth={true}>
          <TextInput
            fullWidth={true}
            label="Coupon Code"
            source="couponCode"
            helperText="Coupon only can contains letters A-Z no spaces or special characters."
          />
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <Labeled label="Description" fullWidth={true}>
          <TextInput source="description" fullWidth={true} />
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="Start Date (UTC)" fullWidth={true}>
          <>
            <DateTimeInput
              source="startDate"
              fullWidth={true}
              defaultValue={defaultStartDate ? new Date() : undefined}
            />
            <LocalisedDateInputPreview source="startDate" label="Start Date" />
          </>
        </Labeled>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Labeled label="End Date (UTC)" fullWidth={true}>
          <>
            <DateTimeInput source="endDate" fullWidth={true} />
            <LocalisedDateInputPreview source="endDate" label="End Date" />
          </>
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <Labeled label="Country" fullWidth={true}>
          <SelectArrayInput
            source="country"
            choices={CountriesSupported}
            fullWidth={true}
            format={(v) => (typeof v === "string" ? v.split(",") : [])}
            parse={(v) => (typeof v === "object" ? v.toString() : "")}
            helperText="Select in which countries the coupon code will be active."
          />
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={12} sm={8}>
        <Labeled label="Products. If left blank, the coupon code will be valid for all products." fullWidth={true}>
          <ProductSelectionArrayInput source="productIds" />
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>

      <Grid item xs={12} sm={12} sx={{ textAlign: "center", fontSize: "14px", color: "#666666", padding: "10px" }}>
        Choose a discount type <br />
        Precentage or Value <br />
        and fill in the details
      </Grid>

      <Grid item xs={6} sm={5} sx={{ textAlign: "right" }}>
        <Labeled label="Percentage Discount">
          <NumberInput min={0} max={100} fullWidth label="Percent Off" source="percentage" />
        </Labeled>
      </Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={6} sm={5}>
        <Labeled label="Value Discount">
          <TextInput
            label="Value Off"
            type="number"
            // value={values.toFixed(2)}
            // onChange={handleChange}
            // inputProps={{
            //   inputComponent: NumberFormatCustom,
            // }}
            format={(v) => (typeof v === "number" ? v / 100 : 0)}
            // format={(v) => (v / 100).toFixed(2)}
            parse={(v) => parseInt((v * 100).toFixed(0))}
            source="value"
          />
        </Labeled>
        {/* <Labeled label="Value Discount">
          <NumberInput min={0} label="Value Off" source="value" />
        </Labeled> */}
      </Grid>

      <Grid item xs={6} sm={5}></Grid>
      <Grid item xs={0} sm={2}></Grid>
      <Grid item xs={6} sm={5}>
        {" "}
        <SelectInput
          source="currency"
          choices={[
            { id: "all", name: "Select a currency" },
            { id: "eur", name: "EUR" },
            { id: "gbp", name: "GBP" },
          ]}
          validate={required()}
          defaultValue={"eur"}
        />
      </Grid>
    </Grid>
  );
};

export const PromotionCodeEdit = () => {
  const [update, { isLoading, error }] = useUpdate();
  const save = useCallback(
    async (values: any) => {
      try {
        await update("promotionCode", { id: values.id, data: values, previousData: values }, { returnPromise: true });
      } catch (error: any) {
        if (error!.body!.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [update]
  );
  return (
    <Edit mutationMode="pessimistic">
      <SimpleForm validate={validatePromotionCodeCreation} toolbar={<MyToolbar />}>
        <PromotionCodeCreateEditForm />
      </SimpleForm>
      {/* <SimpleForm toolbar={<MyToolbar />}>
        <SimpleShowLayout>
          <TextField source="category" />
          <TextField source="couponCode" />
          <TextField source="description" />
          <TextField source="country" />
          <TextField source="percentage" />
          <TextField source="value" />
          <TextField source="currency" />
        </SimpleShowLayout>
      </SimpleForm> */}
    </Edit>
  );
};

const validatePromotionCodeCreation = (values: any) => {
  let errors: any = {};
  const couponRegex = /^[A-Za-z0-9]+$/;
  if (!couponRegex.test(values.couponCode)) {
    errors.couponCode = "Coupon only can contains letters A-Z a-z no spaces or special characters.";
  }
  if (!values.percentage && values.value && values.currency == "all") {
    errors.currency = "Currency must be specified for value-based Promotion Codes";
  }
  if (!values.percentage && !values.value) {
    errors.value = "Precentage or Value must be specified";
    errors.percentage = "Precentage or Value must be specified";
  }
  if (!values.country && values.country !== "") {
    errors.country = "Please select atleast one country";
  }
  if (values.value) {
    console.log(values.value);
    if (parseInt(values.value.toFixed(0)) !== parseFloat(values.value.toFixed(2))) {
      errors.value = "Value can only contains 2 digits. Like 10.55 not 10.5567";
    }
  }
  return errors;
};

export const PromotionCodeCreate = () => {
  const [create] = useCreate();
  const save = useCallback(
    async (values: any) => {
      try {
        await create("promotionCode", { data: values }, { returnPromise: true });
      } catch (error: any) {
        if (error!.body!.errors) {
          // The shape of the returned validation errors must match the shape of the form
          return error.body.errors;
        }
      }
    },
    [create]
  );

  return (
    <Create>
      <SimpleForm validate={validatePromotionCodeCreation}>
        <PromotionCodeCreateEditForm defaultStartDate />
      </SimpleForm>
    </Create>
  );
};
